// client/src/components/public/ShowingBooking.js — "Book a showing" on one of the office's own
// listings: the real free times (lib/showingSlots via /api/public/showings/slots), a day
// and time picker, and a short form: name, email or phone, the consent tick, an optional
// note. Nothing else is asked (fair housing: the same times for everyone, no screening).
// A signed-in visitor (client portal) books with the details on their record through
// /api/client-portal/properties/showings. Requested until the listing agent confirms,
// unless the listing allows instant booking. Renders nothing when online booking is not
// offered, so the plain "Request a showing" form stays the way in.
import React, { useEffect, useMemo, useState } from 'react';
import { portalToken, portalFetch } from '../../utils/properties';

const inputCls = 'mt-1 block w-full rounded-md border border-slate-400 bg-white px-3 py-2 text-slate-900 focus:border-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-600';
const timeOf = (label) => label.replace(/^[A-Za-z]{3}, [A-Za-z]{3} \d{1,2}, /, '');
const dayLabel = (iso) => new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });

export default function ShowingBooking({ listingKey }) {
    const [data, setData] = useState(null);
    const [day, setDay] = useState('');
    const [start, setStart] = useState('');
    const [f, setF] = useState({ name: '', email: '', phone: '', notes: '', consent: false, website: '' });
    const [state, setState] = useState({ sending: false, done: '', error: '' });
    const signedIn = !!portalToken();

    useEffect(() => {
        let live = true;
        fetch(`/api/public/showings/slots?listing_key=${encodeURIComponent(listingKey)}`)
            .then((r) => r.json()).then((j) => { if (live) { setData(j); if (j.days && j.days[0]) setDay(j.days[0].date); } })
            .catch(() => { if (live) setData({ available: false }); });
        return () => { live = false; };
    }, [listingKey]);

    const daySlots = useMemo(() => (data && data.slots ? data.slots.filter((s) => s.local_date === day) : []), [data, day]);
    if (!data || !data.available || !data.slots || !data.slots.length) return null;
    const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
    const instant = data.approval_mode === 'instant';
    const id = (k) => `book-${listingKey}-${k}`;

    const submit = async (e) => {
        e.preventDefault();
        if (!start) { setState({ sending: false, done: '', error: 'Please choose a time.' }); return; }
        setState({ sending: true, done: '', error: '' });
        try {
            let j;
            if (signedIn) {
                j = await portalFetch('/api/client-portal/properties/showings', { method: 'POST',
                    body: JSON.stringify({ listing_key: listingKey, start, notes: f.notes, consent: f.consent }) });
                j.message = j.showing && j.showing.status === 'Confirmed' ? `Booked: ${j.showing.label}.` : `Requested: ${j.showing.label}. The listing agent will confirm the time.`;
            } else {
                const r = await fetch('/api/public/showings/book', { method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ...f, listing_key: listingKey, start }) });
                j = await r.json().catch(() => ({}));
                if (!r.ok) throw new Error(j.message || 'Could not book. Please call our office.');
            }
            setState({ sending: false, done: j.message, error: '' });
        } catch (err) {
            setState({ sending: false, done: '', error: err.message });
            if (/no longer available/i.test(err.message)) {
                fetch(`/api/public/showings/slots?listing_key=${encodeURIComponent(listingKey)}`).then((r) => r.json()).then(setData).catch(() => {});
                setStart('');
            }
        }
    };

    const wrap = (inner) => (
        <section aria-labelledby={id('h')} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <h2 id={id('h')} className="mb-3 text-lg font-semibold text-slate-900">Book a showing</h2>
            {inner}
        </section>
    );
    if (state.done) return wrap(<p className="rounded-md bg-emerald-50 p-4 text-emerald-900" role="status">{state.done}</p>);
    return wrap(
        <form onSubmit={submit} className="space-y-3" aria-label="Book a showing">
            <p className="text-sm text-slate-700">{instant ? 'Pick a time and it is booked.' : 'Pick a time; the listing agent confirms it.'} Times are {data.timezone === 'America/New_York' ? 'Eastern' : 'Central'} time.</p>
            <div>
                <label htmlFor={id('day')} className="block text-sm font-medium text-slate-800">Day</label>
                <select id={id('day')} className={inputCls} value={day} onChange={(e) => { setDay(e.target.value); setStart(''); }}>
                    {data.days.map((d) => <option key={d.date} value={d.date}>{dayLabel(d.date)} ({d.count} open)</option>)}
                </select>
            </div>
            <fieldset>
                <legend className="text-sm font-medium text-slate-800">Time</legend>
                <div className="mt-1 grid grid-cols-3 gap-2">
                    {daySlots.map((s) => (
                        <button type="button" key={s.start} onClick={() => setStart(s.start)} aria-pressed={start === s.start}
                            className={`rounded border px-2 py-1 text-sm ${start === s.start ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-400 bg-white text-slate-900 hover:bg-slate-100'}`}>
                            {timeOf(s.label)}
                        </button>
                    ))}
                </div>
            </fieldset>
            {data.public_note && <p className="text-sm text-slate-700">{data.public_note}</p>}
            {data.occupied_notice && <p className="text-xs text-slate-600">{data.occupied_notice}</p>}
            {!signedIn && (
                <>
                    <div><label htmlFor={id('name')} className="block text-sm font-medium text-slate-800">Name</label>
                        <input id={id('name')} required autoComplete="name" className={inputCls} value={f.name} onChange={set('name')} /></div>
                    <div><label htmlFor={id('email')} className="block text-sm font-medium text-slate-800">Email</label>
                        <input id={id('email')} type="email" autoComplete="email" className={inputCls} value={f.email} onChange={set('email')} /></div>
                    <div><label htmlFor={id('phone')} className="block text-sm font-medium text-slate-800">Phone</label>
                        <input id={id('phone')} type="tel" autoComplete="tel" className={inputCls} value={f.phone} onChange={set('phone')} /></div>
                </>
            )}
            <div><label htmlFor={id('notes')} className="block text-sm font-medium text-slate-800">Anything we should know (optional)</label>
                <textarea id={id('notes')} rows={2} className={inputCls} value={f.notes} onChange={set('notes')} /></div>
            <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px' }}>
                <label htmlFor={id('website')}>Website</label><input id={id('website')} tabIndex={-1} autoComplete="off" value={f.website} onChange={set('website')} />
            </div>
            <label className="flex items-start gap-2 text-xs text-slate-700">
                <input type="checkbox" required checked={f.consent} onChange={set('consent')} className="mt-0.5" />
                <span>{data.consent_text}</span>
            </label>
            {state.error && <p className="text-sm text-red-700" role="alert">{state.error}</p>}
            <button type="submit" disabled={state.sending || !start} className="w-full rounded-md bg-slate-900 px-4 py-2 font-semibold text-white focus:outline-none focus:ring-4 focus:ring-indigo-300 disabled:opacity-60">
                {state.sending ? 'Sending…' : instant ? 'Book this time' : 'Request this time'}
            </button>
        </form>,
    );
}
